import { createSelector } from 'reselect'
import config from 'constants/config'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { runChannelMarkupV2SessionRound } from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupSource, ChannelMarkupV2SessionContext } from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupLeadOffer } from 'lib/channelMarkup/channelMarkupLeadPricing'

export const getChannelMarkup = (state: App.State) => config.ENABLE_CHANNEL_MARKUP ? state.marketing.channelMarkup : undefined

/**
 * The channel markup v2 session (user + market) context used by `getChannelMarkupSource`
 * to run the session round. Memoized on its primitive inputs so it can be composed as an
 * input selector without breaking `getChannelMarkupSource`'s memoization.
 */
export const getChannelMarkupV2SessionContext = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.currentCurrency,
  // Subscriber tier only carries meaning for a member who can currently view LuxPlus+
  // benefits; everyone else evaluates as `null`.
  (state: App.State) => (getCanViewLuxPlusBenefits(state) ? (state.auth.account.luxPlus.member.subscription.item?.tier ?? null) : null),
  (state: App.State) => state.auth.account.roles,
  (region, currency, subscriberTier, roles): ChannelMarkupV2SessionContext => ({ region, currency, subscriberTier, roles }),
)

/**
 * The mode-tagged markup source to thread through the offer mappers. Returns the v2 session
 * state when channel markup v2 is active and the marketing router selected a markup; otherwise
 * falls back to the v1 `channelMarkup`.
 *
 * The v2 markup (`state.marketing.channelMarkupV2`) is decided ONCE at SSR — the marketing
 * router uses weighted-random variant assignment, so evaluating it here on the client would
 * re-roll the variant and drift the price between the SSR render and hydration. This selector
 * only reads that persisted decision and runs the deterministic session round against it.
 *
 * Memoized: the v2 session round rebuilds a fresh object on every call, so reactive
 * `useAppSelector` consumers need a stable reference to avoid re-rendering on every store
 * update. It recomputes only when its inputs (resolved markup, session context, v1 markup)
 * actually change.
 */
export const getChannelMarkupSource = createSelector(
  (state: App.State) => state.marketing.channelMarkupV2,
  (state: App.State) => getChannelMarkupV2SessionContext(state),
  (state: App.State) => getChannelMarkup(state),
  (channelMarkupV2, sessionContext, channelMarkup): ChannelMarkupSource => {
    // The Marketing Router flag alone selects v2: it is the same flag that evaluated the request
    // and tagged the landing, so the rendered price always matches the advertised variant.
    const isV2Active = config.MARKETING_ROUTER_ENABLED
      && !!channelMarkupV2
      && !!sessionContext.region
      && !!sessionContext.currency

    if (isV2Active) {
      // The router has no global offer-id block list, so the v2 source's block list is empty.
      const cfg = { channelMarkupListV1: [], channelMarkupListV2: [channelMarkupV2], globalOfferIdBlockList: [] }
      const { sessionState, channelMarkupId } = runChannelMarkupV2SessionRound(cfg, sessionContext)
      return { mode: 'v2', sessionState, blockList: [], channelMarkupId }
    }

    return { mode: 'v1', channelMarkup }
  },
)

/**
 * The loaded offer a v2 price band reads its lead price from: a Flash/LPC offer, else the bedbank
 * offer or, before it loads, the bedbank summary a search tile renders from.
 */
export function getChannelMarkupLeadOffer(state: App.State, offerId: string): ChannelMarkupLeadOffer | undefined {
  return state.offer.offers[offerId] ?? state.offer.bedbankOffers[offerId] ?? state.offer.bedbankOfferSummaries[offerId]
}
